import apiClient from '@/lib/api-client'
import type { TimeEntry } from '@/shared/types/entities'
import type { ApiResponse } from '@/shared/types/api'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'

export interface StartTimerDto {
  projectId: string
  taskId?: string
  description?: string
  tagIds?: string[]
}

export interface UpdateTimerDto {
  projectId?: string
  taskId?: string
  description?: string
  startTime?: string
}

export interface StopTimerResponse {
  timeEntry: TimeEntry
  duration: number
}

function getWorkspaceId(): string {
  const { selectedWorkspace } = useWorkspaceStore.getState()
  if (!selectedWorkspace) {
    throw new Error('No workspace selected')
  }
  return selectedWorkspace.id
}

export const timerService = {
  async startTimer(data: StartTimerDto): Promise<{ timerId: string; startedAt: string }> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<ApiResponse<{ timerId: string; startedAt: string }>>(
      `/workspaces/${workspaceId}/timers/start`,
      data
    )
    return response.data.data
  },
  
  async stopTimer(): Promise<StopTimerResponse> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<ApiResponse<StopTimerResponse>>(`/workspaces/${workspaceId}/timers/stop`)
    return response.data.data
  },
  
  async cancelTimer(): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/timers/cancel`)
  },

  async pauseTimer(): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/timers/pause`)
  },
  
  async resumeTimer(): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/timers/resume`)
  },
  
  async getActiveTimer(): Promise<{
    isRunning: boolean
    startedAt?: string
    projectId?: string
    taskId?: string
    description?: string
    elapsedSeconds?: number
  } | null> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.get<ApiResponse<{
      startTime: string
      pausedAt: string | null
      projectId?: string
      taskId?: string
      description?: string
      elapsedSeconds?: number
    } | null>>(`/workspaces/${workspaceId}/timers/current`)
    const timer = response.data.data
    if (!timer) return null
    return {
      isRunning: !timer.pausedAt,
      startedAt: timer.startTime,
      projectId: timer.projectId,
      taskId: timer.taskId,
      description: timer.description ?? undefined,
      elapsedSeconds: timer.elapsedSeconds,
    }
  },
  
  async updateTimer(data: UpdateTimerDto): Promise<{ timerId: string; startedAt: string }> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.patch<ApiResponse<{ timerId: string; startedAt: string }>>(
      `/workspaces/${workspaceId}/timers/current`,
      data
    )
    return response.data.data
  },

  async autoSave(description: string, elapsedSeconds: number): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/timers/autosave`, {
      description,
      elapsedSeconds,
    })
  },
}

export default timerService
